import React from "react";
import ApiFactory from "neon-frontend-utils/src/api/factory";
import RedirectHelper from "neon-frontend-utils/src/redirectHelper";
import RedirectRenderer from "../../utils/RedirectRenderer";

/**
 * ProtectedRoute
 *
 * Wrapper for a route that should only be accessible for certain roles, as specified in AppConfig
 *
 * @param {Object} props - The props object.
 * @param {string} props.componentName - The name that should match the handle in AppConfig.
 * @param {string} [props.redirectPath='/'] - The path to redirect in case of authorization failure.
 * @param {*} [props.children] - The component to render in case of authorization success.
 * @returns {undefined}
 *
 * @example
 * <ProtectedRoute
 *     api={api}
 *     componentName="participations"
 * >
 *     <ParticipationsRoute/>
 * </ProtectedRoute>
 *
 * note: you can also use it inside a React-Router Route: <Route element={...}/>
 */
const ProtectedRoute = (props) => {
    const api = ApiFactory.get("neon");
    const { componentName, redirectPath = "/" } = props;

    let redirect = redirectPath;

    if (!componentName) {
        throw new Error("<ProtectedRoute> componentName property must be defined.");
    }

    redirect = api.getAuthoriser().getLoginRedirect();

    const authenticated = api.getAuthenticator().isAuthenticated(); // also check authentication here
    const authorised = api.getAuthoriser().authorise(api.getAuthenticator().getUser(), componentName, "route");

    if (!authenticated) {
        RedirectHelper.instance.setRedirectPath(window.location.pathname);
        redirect = api.getAuthoriser().getLoginRedirect();
    }

    // if the user is logged in as a tenant user, but didn't select a tenant yet, go/redirect back to login flow
    const user = api.getAuthenticator().getUser();

    if (user && user.getIsTenantUser() && !user.getActiveOrganisationTenant()) {
        RedirectHelper.instance.setRedirectPath(window.location.pathname);
        redirect = api.getAuthoriser().getTenantSelectionRedirect();

        if (!redirect) {
            throw new Error("Invalid tenantSelectionRedirect provided in ProtectedRoute");
        }
    }

    if (!authorised) {
        RedirectRenderer.render(redirect || api.getAuthoriser().getLoginRedirect(), true, null);
    }

    return <React.Fragment>{props.children}</React.Fragment>;
};

export default ProtectedRoute;
